import type { ReactNode } from 'react';
import type { CreatorFacts, CreatorMore } from '../contract';
import { fmtKc, fmtNum, fmtShare } from '../format';
import { EfficiencyList } from './efficiency-section';
import { MetricRow, PctBadge } from './metric-row';
import { regionLabel } from './overseas-section';
import styles from '../style.module.scss';
import more from '../style-more.module.scss';

const Card = ({ title, children }: { title: string; children: ReactNode }) => (
  <section className={styles.card} aria-label={title}>
    <h2 className={styles.cardTitle}>{title}</h2>
    {children}
  </section>
);

/** `monthFile`: số theo file tháng TikTok — khu vực #1 là của cả giai đoạn file, không đếm theo ngày. */
const OverseasCard = ({ m, monthFile }: { m: CreatorMore; monthFile: boolean }) => (
  <Card title="KC quốc tế">
    <MetricRow
      label="KC từ nước ngoài"
      value={fmtKc(m.totals.overseasDiamonds)}
      hint={`${fmtShare(m.shares.overseasPct)} KC của Creator`}
    />
    {m.regions.length ? (
      <ol className={more.regionList} aria-label="Khu vực khách quốc tế">
        {m.regions.map((r) => (
          <li key={r.code}>
            <span className={more.region}>{regionLabel(r)}</span>
            <span className={more.metricHint}>
              {r.daysFirst > 0
                ? monthFile
                  ? '#1 cả tháng (file tháng)'
                  : `#1 ở ${fmtNum(r.daysFirst)} ngày`
                : 'trong top 3'}
            </span>
          </li>
        ))}
      </ol>
    ) : (
      <p className={styles.muted}>Chưa có khu vực.</p>
    )}
    <p className={more.note}>TikTok chỉ cho thứ hạng khu vực, không cho số KC từng nước.</p>
  </Card>
);

const FanCard = ({ m }: { m: CreatorMore }) => (
  <Card title="Fan">
    <MetricRow
      label="Tổng fan"
      value={fmtNum(m.fans.totalFans)}
      hint={
        m.fans.totalFansStart !== null && m.fans.totalFansStart !== undefined
          ? `đầu tháng ${fmtNum(m.fans.totalFansStart)}`
          : undefined
      }
    />
    {m.fans.growthPct !== undefined && m.fans.growthPct !== null && (
      <MetricRow label="Tăng trưởng fan" value={<PctBadge pct={m.fans.growthPct} />} />
    )}
    <MetricRow label="Fan mới" value={fmtNum(m.totals.newFans)} />
    <MetricRow
      label="Fan Club hoạt động"
      value={fmtNum(m.fans.activeFanClubFans)}
      hint={
        m.fans.activeRatioPct !== null && m.fans.activeRatioPct !== undefined
          ? `${fmtShare(m.fans.activeRatioPct)} tổng fan`
          : undefined
      }
    />
    <MetricRow
      label="KC từ Fan Club"
      value={fmtShare(m.shares.fanClubPct)}
      hint={`${fmtKc(m.totals.fanClubDiamonds)} KC`}
    />
  </Card>
);

const MultiGuestCard = ({ m }: { m: CreatorMore }) => (
  <Card title="Đa khách">
    <MetricRow
      label="KC phòng đa khách"
      value={fmtShare(m.shares.multiGuestPct)}
      hint={`${fmtKc(m.totals.multiGuestHost + m.totals.multiGuestGuest)} KC`}
    />
    <MetricRow label="Là chủ phòng" value={fmtKc(m.totals.multiGuestHost)} />
    <MetricRow label="Là khách" value={fmtKc(m.totals.multiGuestGuest)} />
  </Card>
);

const StatusCard = ({ m }: { m: CreatorMore }) => (
  <Card title="Trạng thái TikTok">
    <MetricRow label="Bậc" value={m.status.tier || '—'} />
    <MetricRow label="Trưởng thành" value={m.status.maturity || '—'} />
    <MetricRow
      label="Creator LIVE mới"
      value={
        m.status.isNewLiveCreator === null || m.status.isNewLiveCreator === undefined
          ? '—'
          : m.status.isNewLiveCreator
          ? 'Có'
          : 'Không'
      }
    />
    {m.agent && <MetricRow label="Người đại diện" value={m.agent} />}
    <p className={more.note}>
      {m.status.source === 'MONTH_FILE'
        ? 'Theo file tháng TikTok.'
        : 'Theo file ngày mới nhất — bậc chỉ có ý nghĩa ở file tháng (cuối tháng).'}
    </p>
  </Card>
);

/** Các thẻ cột sau của file TikTok trong Drawer (đặc tả 04 §5, 06 E1/E4). Thiếu `more` → không vẽ. */
const CreatorMoreCards = ({ f }: { f: CreatorFacts }) => {
  const m = f.more;
  if (!m) return null;
  return (
    <>
      <div className={more.cardGrid}>
        <OverseasCard m={m} monthFile={f.basis === 'MONTH_FILE'} />
        <FanCard m={m} />
        <MultiGuestCard m={m} />
        <StatusCard m={m} />
      </div>
      {m.efficiency && (
        <Card title="Hiệu suất">
          <EfficiencyList curr={m.efficiency} prev={m.efficiencyPrev} />
          {m.efficiencyPrev && f.compare && (
            <p className={more.note}>Tháng trước: {f.compare.label}.</p>
          )}
        </Card>
      )}
    </>
  );
};

export default CreatorMoreCards;
